import _ from 'lodash'

export class ApiError extends Error {
  constructor(...args) {
    const e = args[0] instanceof Error ? args.shift() : undefined
    const [path, response, detail] = args
    super() // calling super with message doesn't work
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError)
    }
    // prettier-ignore
    this.message =
            e ? `${path} ${e.message}` :
            response ? `${path} ${response.status}` :
            path
    this.response = response
    this.detail = detail
    this.name = 'ApiError' // for Rollbar
  }
}

export function api(path, params = {}, options) {
  options = {
    json: true,
    ...options,
  }

  const qs = toQueryString(params)

  return window
    .fetch(`/api/${path}${qs.length ? '?' : ''}${qs}`, {
      credentials: 'include', // send cookies!
    })
    .catch(e => {
      throw new ApiError(e, path)
    })
    .then(r => {
      if (r.ok && !options.json) {
        return r.text()
      } else if (r.ok) {
        return r.json().catch(e => {
          // JSON decode failed.
          return r
            .text()
            .catch(() => undefined)
            .then(detail => {
              throw new ApiError(e, path, r, detail)
            })
        })
      } else {
        // Unexpected HTTP status.
        return r
          .text()
          .catch(() => undefined)
          .then(detail => {
            throw new ApiError(path, r, detail)
          })
      }
    })
}

export function pseudoApi(path) {
  return window
    .fetch(path, {credentials: 'include'})
    .catch(e => {
      throw new ApiError(e, path)
    })
    .then(r => {
      if (r.ok) {
        return r.text()
      } else {
        // Unexpected HTTP status.
        return r
          .text()
          .catch(() => undefined)
          .then(detail => {
            throw new ApiError(path, r, detail)
          })
      }
    })
}

/**
 * Convert JSON structure from kebab-case to camelCase. Works on arrays and
 * objects recursively. Does not mutate the original object.
 *
 * @param {*} obj - Array or object to be converted.
 * @returns {*} same type as given
 */
export function camel(obj) {
  if (obj === null || obj === undefined) {
    return obj
  } else if (Array.isArray(obj)) {
    return obj.map(camel)
  } else if (_.isPlainObject(obj)) {
    return Object.entries(obj)
      .map(([k, v]) => [camelCase(k), camel(v)])
      .reduce((o, [k, v]) => {
        if (k in o && !_.isEqual(o[k], v)) {
          console.warn(`camel: conflicting values for ${k}`)
        }
        o[k] = v
        return o
      }, {})
  }
  return obj
}

export function camelCase(k) {
  return typeof k === 'string'
    ? k.replace(/[-_\s]+(\w|$)/g, (match, letter) => letter.toUpperCase())
    : k
}

/**
 * Convert array or object to query string, with keys/values properly
 * escaped. For objects, sorts the keys. Values can be arrays, in which
 * case the keys will be repeated in the query string.
 *
 * @param {(Object|Array)} - obj
 * @returns {string} - query string without leading ?
 */
export function toQueryString(pairs) {
  const qs = new window.URLSearchParams()

  // When passed an object of key/value pairs, sort the entries to get
  // consistent API calls.
  if (!isIterable(pairs)) {
    pairs = _(pairs)
      .toPairs()
      .sortBy(x => x[0])
      .value()
  }

  // Each value should be either a string, number or Array.
  for (let [key, values] of pairs) {
    if (typeof values === 'string' || typeof values === 'number') {
      values = [values]
    } else if (!Array.isArray(values)) {
      console.error(`toQueryString values=${values} (${typeof values})`)
      if (!isIterable(values)) {
        values = [values]
      }
    }
    for (const value of values) {
      qs.append(key, value)
    }
  }

  return qs.toString()
}

/**
 * Check if object is iterable (usually array, could be string, caller
 * should handle if string should be excluded).
 *
 * @param {*} obj - anything
 * @returns {boolean}
 */
export function isIterable(obj) {
  return (
    obj !== undefined &&
    obj !== null &&
    typeof obj[Symbol.iterator] === 'function'
  )
}

export default {
  api,
  camel,
  pseudoApi,
}
